import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Router, UrlTree } from '@angular/router';
import { Observable, firstValueFrom, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthStateService } from './auth-state.service';
import { TenantService } from './tenant.service';
import { EncryptionService } from './encryption.service';
import { StorageService } from './storage.service';
import { DateUtilityService } from './date-utility.service';
import { MenuService } from './menu.service';
import { GuestFirebaseAuthService } from './guest-firebase-auth.service';
import { Tenant } from '../models/tenant.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  PatientMfaChannelRequest,
  PatientMfaChannelValidationResponse,
  PatientMfaOtpResponse,
  PatientMfaSendOtpRequest,
  PatientMfaValidateOtpRequest,
  PatientMfaPreferenceRequest,
  PatientMfaPreferenceResponse,
  MFA_PENDING_AUDIT_EVENTS_KEY
} from '../models/patient-mfa.model';
import { SKIP_HTTP_ERROR_TOAST } from '../utils/http-error-toast.context';
import { clearRescheduleAppointmentSession } from '../utils/reschedule-cancel-policy.util';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /** Resolved from current tenant: hosted uses apiUrl, non-hosted uses apiUrlNonHosted */
  private get apiUrl(): string {
    return this.tenantService.getApiUrl();
  }
  private userInfoKey = 'user_info';
  /** Unencrypted — used to return to sign-in after browser back from MFA / associated patients. */
  private readonly tenantShortNameSessionKey = 'tenant_short_name';
  private readonly signInReturnShortNameKey = 'sign_in_return_short_name';

  // Cache for logo URLs and template messages (keyed by tenant code)
  private logoUrlCache = new Map<string, any>();
  private templateMessageCache = new Map<string, SafeHtml | null>();

  private authStateService = inject(AuthStateService);
  private tenantService = inject(TenantService);
  private encryptionService = inject(EncryptionService);
  private storageService = inject(StorageService);
  private dateUtility = inject(DateUtilityService);
  private menuService = inject(MenuService);
  private sanitizer = inject(DomSanitizer);
  // guest_pi / guest_uid must not outlive portal logout
  private guestAuth = inject(GuestFirebaseAuthService);

  constructor(
    private http: HttpClient,
    private router: Router,
  ) { }

  /** Persist sign-in URL segment for post-login browser-back handling. */
  rememberTenantShortNameForSignIn(shortName: string | null | undefined): void {
    if (shortName?.trim()) {
      const name = shortName.trim();
      sessionStorage.setItem(this.tenantShortNameSessionKey, name);
      sessionStorage.setItem(this.signInReturnShortNameKey, name);
    }
  }

  /** Reads tenant sign-in path from session before clearPortalSession (browser back from associated patients). */
  private readSignInShortNameForRedirect(): string | null {
    const fromReturn = sessionStorage.getItem(this.signInReturnShortNameKey)?.trim();
    if (fromReturn) {
      return fromReturn;
    }
    const fromShort = sessionStorage.getItem(this.tenantShortNameSessionKey)?.trim();
    if (fromShort) {
      return fromShort;
    }
    const fromTenant = this.tenantService.getCurrentTenant()?.tenantShortName?.trim();
    return fromTenant || null;
  }

  /** Clears portal session without navigation. */
  clearPortalSession(): void {
    this.storageService.removeSessionStorageByKey(this.userInfoKey);
    this.storageService.removeSessionStorageByKey('tenant_code');
    this.storageService.removeSessionStorageByKey('tenant_id');
    this.storageService.removeSessionStorageByKey('user_permissions');
    this.storageService.removeSessionStorageByKey('user_icon_settings');
    this.storageService.removeSessionStorageByKey('user_other_settings');
    this.storageService.removeSessionStorageByKey('associated_patients');
    this.storageService.removeSessionStorageByKey('pending_mfa_login');
    this.storageService.removeSessionStorageByKey(MFA_PENDING_AUDIT_EVENTS_KEY);
    this.storageService.removeSessionStorageByKey('mfa_pending_otp_verify');
    this.storageService.removeSessionStorageByKey('has_seen_welcome_modal');
    this.storageService.removeSessionStorageByKey('mfa_reminder_dismissed_session');
    sessionStorage.removeItem('tenant_type');
    sessionStorage.removeItem(this.tenantShortNameSessionKey);
    // PE-6231 — do not leave original appointment cancel payload after logout
    clearRescheduleAppointmentSession();
    // sign_in_return_short_name kept until sign-in page loads (browser back from associated patients)
    this.storageService.clearSessionId();
    // Clear `dm_email` / `_grecaptcha`
    this.storageService.clearLoggedOutBrowserKeys();
    // Guest booking keys are not for existing-patient sessions
    void this.guestAuth.clearGuestSession();
    this.authStateService.logout();
    this.tenantService.clearCurrentTenant();
    this.menuService.resetMenuState();
  }

  /**
   * Ends session and returns a UrlTree to the tenant sign-in route (sync — for route guards / browser back).
   */
  createSignInUrlTree(): UrlTree | null {
    const shortName = this.readSignInShortNameForRedirect();
    this.clearPortalSession();
    if (shortName) {
      return this.router.createUrlTree([`/${shortName}`], {
        queryParams: { sessionEnded: '1' }
      });
    }
    return null;
  }

  /** Navigate to sign-in after browser back from associated patients (no page-not-found). */
  returnToSignInPage(): void {
    const shortName = this.readSignInShortNameForRedirect();
    if (!shortName) {
      void this.logout();
      return;
    }
    this.clearPortalSession();
    window.location.replace(`/${shortName}?sessionEnded=1`);
  }

  /** Clears sign-in return path after landing on login (post browser back). */
  clearSignInReturnShortName(): void {
    sessionStorage.removeItem(this.signInReturnShortNameKey);
  }

  getUserLogin(username: string, password: string, tenantCode: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt username, password, and tenantcode using AES-256-GCM encryption
    const encryptedUsername = await this.encryptionService.encryptAes256Gcm(username);
    const encryptedPassword = await this.encryptionService.encryptAes256Gcm(password);
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);

    const loginData = {
      susername: encryptedUsername,
      spassword: encryptedPassword,
      tenantcode: encryptedTenantCode,
      nuserid: 0,
      sapptype: "PatientPortal",
      sapplicationcode: "",
    };

    const url = `${this.apiUrl}/auth/login`;

    // PE-6155 — sign-in maps credentials / portal-config messages itself
    return await firstValueFrom(this.http.post<any>(url, loginData, {
      context: new HttpContext().set(SKIP_HTTP_ERROR_TOAST, true),
    }));
  
    })());
  }

  // PE-00 — SPA sign-in navigation after logout (no full page reload)
  async logout(): Promise<void> {
    const currentTenant = this.tenantService.getCurrentTenant();
    const storedTenantCode = this.storageService.getSessionStorage('tenant_code');
    const tenantCode = currentTenant?.tenantCode || storedTenantCode;
    const tenantShortName = this.readSignInShortNameForRedirect();

    this.clearPortalSession();

    if (tenantShortName) {
      await this.router.navigate([`/${tenantShortName}`], {
        replaceUrl: true,
        state: currentTenant ? { tenant: currentTenant } : undefined
      });
      return;
    }

    if (tenantCode) {
      const tenant = await firstValueFrom(this.tenantService.getTenantByCode(tenantCode));
      if (tenant?.tenantShortName) {
        await this.router.navigate([`/${tenant.tenantShortName}`], {
          replaceUrl: true,
          state: { tenant }
        });
        return;
      }
    }

    await this.router.navigate(['/page-not-found'], { replaceUrl: true });
  }

  isLoggedIn(): boolean {
    return this.authStateService.isLoggedIn;
  }

  getUserInfo(): any {
    return this.authStateService.userInfo;
  }

  /**
   * True when the signed-in user is a patient representative (before or after patient selection).
   */
  isPatientRepresentative(): boolean {
    const userInfo = this.getUserInfo() || this.storageService.getSessionStorage(this.userInfoKey);
    if (!userInfo) {
      return false;
    }
    const hasAssociatedPatients = this.storageService.getSessionStorage('associated_patients');
    return !!(hasAssociatedPatients
      || (userInfo.prid && userInfo.patientId && String(userInfo.prid) !== String(userInfo.patientId)));
  }

  /**
   * MFA preference / sign-in OTP subject: representative nPRID, otherwise patient PatientUID.
   * Account Settings MFA for reps is rep-level only — not tied to the selected patient.
   */
  getMfaSubjectId(): string | null {
    const userInfo = this.getUserInfo() || this.storageService.getSessionStorage(this.userInfoKey);
    if (!userInfo) {
      return null;
    }
    if (this.isPatientRepresentative() && userInfo.prid) {
      return String(userInfo.prid);
    }
    return userInfo.patientId ? String(userInfo.patientId) : null;
  }

  /**
   * Get prid (Patient Representative ID) from userInfo
   * For patient representatives: prid = nUserID from login, patientId = selected patient's PatientUID
   * For normal users: return "0"
   * Returns string as prid is always returned as string from API
   */
  getPrid(): string {
    const userInfo = this.getUserInfo() || this.storageService.getSessionStorage(this.userInfoKey);

    if (!userInfo) {
      return "0";
    }

    if (this.isPatientRepresentative() && userInfo.prid) {
      return String(userInfo.prid);
    }

    // For normal users, return "0"
    return "0";
  }

  getSessionId(): string | null {
    const userInfo = this.storageService.getSessionStorage(this.userInfoKey);
    return userInfo?.sessionId || null;
  }

  portalSendIP(patientUID: string, prid: string, sIP: string, tenantId: string): Observable<any> {
    const requestData = {
      patientuid: patientUID,
      prid: prid,
      sip: sIP,
      tenantid: tenantId
    };

    const url = `${this.apiUrl}/auth/PortalSendIP`;

    // sessionId will be automatically added by session interceptor
    return this.http.post<any>(url, requestData);
  }

  changePassword(patientId: string, oldPassword: string, newPassword: string, tenantId: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt passwords using AES-256-GCM encryption
    const encryptedOldPassword = await this.encryptionService.encryptAes256Gcm(oldPassword);
    const encryptedNewPassword = await this.encryptionService.encryptAes256Gcm(newPassword);

    const changePasswordData = {
      sapplicationcode: "",
      nprid: 0,
      sapptype: "PatientPortal",
      tenantid: tenantId,
      loginusertypefield: "patient",
      onlineaccessfield: true,
      portaluseruidfield: patientId,
      practiceusuuidfield: patientId,
      isresetpasswordfield: false,
      ischangepasswordfield: true,
      chosenpasswordfield: encryptedNewPassword,
      oldpasswordfield: encryptedOldPassword,
      activitydatetime: `/Date(${new Date().getTime()}-0000)/`
    };

    const url = `${this.apiUrl}/accountsettings/putaccess`;

    // sessionId will be automatically added by session interceptor
    return await firstValueFrom(this.http.post<any>(url, changePasswordData));
  
    })());
  }

  getSecurityQuestionRecovery(username: string, tenantId: string): Observable<any> {
    const securityData = {
      susername: username,
      sapplicationcode: "",
      sapptype: "patientportal",
      tenantid: tenantId,
      sessionid: this.getSessionId()
    };

    const url = `${this.apiUrl}/auth/getsecurityquestionrecovery`;

    // sessionId will be automatically added by session interceptor
    return this.http.post<any>(url, securityData);
  }

  resetSecurityAnswer(
    patientId: string,
    username: string,
    securityQuestion: string,
    securityAnswer: string,
    tenantId: string
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt username, security question, and security answer using AES-256-GCM encryption
    const encryptedUsername = await this.encryptionService.encryptAes256Gcm(username);
    const encryptedSecurityQuestion = await this.encryptionService.encryptAes256Gcm(securityQuestion);
    const encryptedSecurityAnswer = await this.encryptionService.encryptAes256Gcm(securityAnswer);

    const resetSecurityData = {
      patientuid: patientId,
      sapplicationcode: "",
      sapptype: "PatientPortal",
      tenantid: tenantId,
      prid: 0,
      activitydatetime: this.dateUtility.getActivityDateTime(),
      portalaccessusernamefield: encryptedUsername,
      securityanswerfield: encryptedSecurityAnswer,
      securityquestionfield: encryptedSecurityQuestion,
      isquickactivate: 1,
      practiceid: "",
      ipaddress: ""
    };

    const url = `${this.apiUrl}/auth/reset-security-info-v2`;

    // sessionId will be automatically added by session interceptor
    return await firstValueFrom(this.http.post<any>(url, resetSecurityData));
  
    })());
  }

  resetSecurityAnswerQuickActivation(
    patientId: string,
    username: string,
    password: string,
    securityQuestion: string,
    securityAnswer: string,
    tenantId: string
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt password, username, security question, and security answer using AES-256-GCM encryption
    const encryptedPassword = await this.encryptionService.encryptAes256Gcm(password);
    const encryptedUsername = await this.encryptionService.encryptAes256Gcm(username);
    const encryptedSecurityQuestion = await this.encryptionService.encryptAes256Gcm(securityQuestion);
    const encryptedSecurityAnswer = await this.encryptionService.encryptAes256Gcm(securityAnswer);

    const resetSecurityData = {
      patientuid: patientId,
      sapplicationcode: "",
      sapptype: "PatientPortal",
      sessionid: "",
      tenantid: tenantId,
      portalpassword: encryptedPassword,
      prid: 0,
      activitydatetime: `/Date(${new Date().getTime()}-0000)/`,
      portalaccessusernamefield: encryptedUsername,
      securityanswerfield: encryptedSecurityAnswer,
      securityquestionfield: encryptedSecurityQuestion,
      isquickactivate: true,
      practiceid: "",
      ipaddress: null
    };

    const url = `${this.apiUrl}/auth/reset-security-answer-quickactivation`;

    // Set sessionid header to empty string to override the session interceptor
    const headers = new HttpHeaders({
      'sessionid': ''
    });

    return await firstValueFrom(this.http.post<any>(url, resetSecurityData, { headers }));
  
    })());
  }

  getLogoSignUrl(tenantCode: string): Observable<any> {
    // Check cache first - return cached value if exists
    if (this.logoUrlCache.has(tenantCode)) {
      return new Observable<any>(observer => {
        observer.next(this.logoUrlCache.get(tenantCode));
        observer.complete();
      });
    }

    // Cache miss - fetch from API and cache the result
    const url = `${this.apiUrl}/getlogosignurl?tenantcode=${tenantCode}`;
    return this.http.get<any>(url).pipe(
      map((response: any) => {
        // Cache the response for this tenant code
        if (response) {
          this.logoUrlCache.set(tenantCode, response);
        }
        return response;
      })
    );
  }

  /**
   * Clear logo URL cache (useful when tenant changes or on manual refresh)
   */
  clearLogoUrlCache(tenantCode?: string): void {
    if (tenantCode) {
      this.logoUrlCache.delete(tenantCode);
    } else {
      this.logoUrlCache.clear();
    }
  }

  getDocuments(tenantCode: string): Observable<any> {
    const url = `${this.apiUrl}/getdocuments?tenantcode=${tenantCode}`;
    return this.http.get<any>(url);
  }

  getHelpInfoDoc(tenantCode: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt tenantcode using AES-256-GCM encryption
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);
    const url = `${this.apiUrl}/gethelpinfodoc?tenantcode=${encryptedTenantCode}`;
    return await firstValueFrom(this.http.get<any>(url));
  
    })());
  }

  checkPatientExists(patientuid: string, guid: string, tenantcode: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt patientuid and tenantcode using AES-256-GCM encryption
    const encryptedPatientUID = await this.encryptionService.encryptAes256Gcm(patientuid);
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantcode);

    const payload = {
      patientuid: encryptedPatientUID,
      guid: guid,
      tenantcode: encryptedTenantCode
    };

    const url = `${this.apiUrl}/registration/portalpatientexist`;
    return await firstValueFrom(this.http.post<any>(url, payload));
  
    })());
  }

  createPortalAccount(
    patientuid: string,
    portalaccessusername: string,
    strportalaccesspassword: string,
    securityquestion: string,
    securityanswer: string,
    guid: string,
    appcode: string,
    apptype: string,
    onlineaccess: boolean,
    isblocked: boolean,
    tenantcode: string
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt patientuid and tenantcode using AES-256-GCM encryption
    const encryptedPatientUID = await this.encryptionService.encryptAes256Gcm(patientuid);
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantcode);

    const payload = {
      patientuid: encryptedPatientUID,
      portalaccessusername: portalaccessusername,
      strportalaccesspassword: strportalaccesspassword,
      securityquestion: securityquestion,
      securityanswer: securityanswer,
      guid: guid,
      appcode: appcode,
      apptype: apptype,
      onlineaccess: onlineaccess,
      isblocked: isblocked,
      tenantcode: encryptedTenantCode
    };

    const url = `${this.apiUrl}/registration/portalpatientcreateac`;
    return await firstValueFrom(this.http.post<any>(url, payload));
  
    })());
  }

  // PE-6139 — getpatientcount via PE2; guest Bearer attached by guestAuthInterceptor (guest routes only)
  getPatientCount(email: string, tenantCode: string): Observable<any> {
    return from((async () => {
      // PE-6139 — never create/attach guest token after sign-in or on authGuard sessions
      if (!this.guestAuth.hasPortalSession()) {
        await this.guestAuth.ensureAnonymousSession();
      }
      const encryptedEmail = await this.encryptionService.encryptAes256Gcm(email);
      const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);
      const base = environment.pebackend_url.endsWith('/')
        ? environment.pebackend_url
        : `${environment.pebackend_url}/`;
      const url =
        `${base}auth/getpatientcount` +
        `?email=${encodeURIComponent(encryptedEmail)}` +
        `&tenantcode=${encodeURIComponent(encryptedTenantCode)}`;
      return await firstValueFrom(this.http.get<any>(url));
    })());
  }

  sendEmailForForgotPassword(username: string, tenantCode: string, tenantShortName: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt username and tenantcode using AES-256-GCM encryption
    const encryptedUsername = await this.encryptionService.encryptAes256Gcm(username);
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);

    const payload = {
      username: encryptedUsername,
      tenantcode: encryptedTenantCode,
      tenantshortname: tenantShortName
    };

    const url = `${this.apiUrl}/auth/sendemailforgotpassword`;
    return await firstValueFrom(this.http.post<any>(url, payload));
  
    })());
  }

  sendEmailForForgotPasswordV2(username: string, tenantCode: string, tenantShortName: string, loginName: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt username, tenantcode, and loginname using AES-256-GCM encryption
    const encryptedUsername = await this.encryptionService.encryptAes256Gcm(username);
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);
    const encryptedLoginName = await this.encryptionService.encryptAes256Gcm(loginName);

    const payload = {
      username: encryptedUsername,
      tenantcode: encryptedTenantCode,
      tenantshortname: tenantShortName,
      loginname: encryptedLoginName
    };

    const url = `${this.apiUrl}/auth/sendemailforgotpassword_v2`;
    return await firstValueFrom(this.http.post<any>(url, payload));
  
    })());
  }

  updatePassword(
    patientSessionId: string,
    patientId: string,
    tenantCode: string,
    tenantShortName: string,
    newPassword: string
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt patientsessionid, patientid, tenantcode, and newpassword using AES-256-GCM encryption
    const encryptedPatientSessionId = await this.encryptionService.encryptAes256Gcm(patientSessionId);
    const encryptedPatientId = await this.encryptionService.encryptAes256Gcm(patientId);
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);
    const encryptedNewPassword = await this.encryptionService.encryptAes256Gcm(newPassword);

    const payload = {
      patientsessionid: encryptedPatientSessionId,
      patientid: encryptedPatientId,
      tenantcode: encryptedTenantCode,
      tenantshortname: tenantShortName, // plain text from route params
      newpassword: encryptedNewPassword
    };

    const url = `${this.apiUrl}/auth/update-password`;
    return await firstValueFrom(this.http.put<any>(url, payload));
  
    })());
  }

  checkLinkStatus(
    patientSessionId: string,
    patientId: string,
    tenantCode: string
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt patientsessionid, patientid, and tenantcode using AES-256-GCM encryption
    const encryptedPatientSessionId = await this.encryptionService.encryptAes256Gcm(patientSessionId);
    const encryptedPatientId = await this.encryptionService.encryptAes256Gcm(patientId);
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);

    const payload = {
      patientsessionid: encryptedPatientSessionId,
      patientid: encryptedPatientId,
      tenantcode: encryptedTenantCode
    };

    const url = `${this.apiUrl}/auth/checklinkstatus`;
    return await firstValueFrom(this.http.post<any>(url, payload));
  
    })());
  }

  // PE-6220 — optional securityAnswer is AES-256-GCM-encrypted and verified on the server
  getSecInfoDetails(username: string, tenantCode: string, securityAnswer?: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => { // any: existing SimpleResponse shape
    const payload: Record<string, string> = {
      susername: username,
      tenantcode: tenantCode
    };
    const trimmedAnswer = securityAnswer?.trim();
    if (trimmedAnswer) {
      payload['securityanswerfield'] = await this.encryptionService.encryptAes256Gcm(trimmedAnswer);
    }

    const url = `${this.apiUrl}/auth/getsecinfodetails`;
    return await firstValueFrom(this.http.post<any>(url, payload)); // any: existing SimpleResponse shape
  
    })());
  }

  updateSecInfo(
    patientUID: string,
    tenantCode: string,
    newPassword: string,
    prid: string = "0"
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt password using AES-256-GCM encryption
    const encryptedPassword = await this.encryptionService.encryptAes256Gcm(newPassword);

    const payload = {
      patientuid: patientUID,
      sapplicationcode: "",
      sessionid: "",
      sapptype: "",
      tenantcode: tenantCode,
      portalpassword: encryptedPassword,
      prid: prid,
      activitydatetime: this.dateUtility.getActivityDateTime(),
      portalaccessusernamefield: "",
      securityanswerfield: "",
      securityquestionfield: "",
      isquickactivate: "false",
      practiceid: "",
      ipaddress: ""
    };

    const url = `${this.apiUrl}/auth/updateSecInfo`;
    return await firstValueFrom(this.http.post<any>(url, payload));
  
    })());
  }

  decryptSecurityAnswer(encryptedAnswer: string): Promise<string> {
    // PE-6337 — AES-GCM async
    return this.encryptionService.decryptAes256Gcm(encryptedAnswer);
  }

  getPracticeTime(tenantCode: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt tenantcode using AES-256-GCM encryption
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);

    const payload = {
      tenantcode: encryptedTenantCode
    };

    const url = `${this.apiUrl}/auth/getpracticetime`;
    return await firstValueFrom(this.http.post<any>(url, payload));
  
    })());
  }

  getTemplateMessages(tenantCode: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt tenantCode using AES-256-GCM encryption (consistent with other API calls)
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);
    const url = `${this.apiUrl}/template/gettemplatemessage?tenantcode=${encryptedTenantCode}`;
    return await firstValueFrom(this.http.get<any>(url));
  
    })());
  }

  getTemplateMessageforportal(tenantId: string): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    // Encrypt tenantCode using AES-256-GCM encryption (consistent with other API calls)
    //   const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);
    const url = `${this.apiUrl}/template/gettemplatemessagesforportalv2?tenantid=${tenantId}`;
    return await firstValueFrom(this.http.get<any>(url));
  
    })());
  }


  /**
   * Get a specific template message by message ID
   * Returns SafeHtml for safe rendering in templates
   * @param messageId - The nPortalMessageId (1 = Portal Secure Message, 2 = Portal Login, 3 = Patient Activation)
   * @param tenantCode - Optional tenant code. If not provided, will try to get from current tenant
   * @returns Observable<SafeHtml | null>
   */
  getTemplateMessageById(messageId: number, tenantCode?: string): Observable<SafeHtml | null> {
    // If tenantCode not provided, try to get from current tenant/session
    if (!tenantCode) {
      const currentTenant = this.tenantService.getCurrentTenant();
      tenantCode = currentTenant?.tenantCode || this.storageService.getSessionStorage('tenant_code');
    }

    const tenantId = this.storageService.getSessionStorage('tenant_id');

    if (!tenantCode && !tenantId) {
      return new Observable<SafeHtml | null>(observer => {
        observer.next(null);
        observer.complete();
      });
    }

    // Build cache key: messageId + tenantCode (or tenantId for portal messages)
    const cacheKey = tenantId && [1, 6, 7, 8].includes(messageId)
      ? `template_${messageId}_tenantId_${tenantId}`
      : tenantCode
        ? `template_${messageId}_${tenantCode}`
        : null;

    // Check cache first
    if (cacheKey && this.templateMessageCache.has(cacheKey)) {
      return new Observable<SafeHtml | null>(observer => {
        observer.next(this.templateMessageCache.get(cacheKey) || null);
        observer.complete();
      });
    }

    const templateRequest = [1, 6, 7, 8].includes(messageId)
      ? (tenantId ? this.getTemplateMessageforportal(tenantId) : new Observable<SafeHtml | null>(observer => {
        observer.next(null);
        observer.complete();
      }))
      : (tenantCode ? this.getTemplateMessages(tenantCode) : new Observable<SafeHtml | null>(observer => {
        observer.next(null);
        observer.complete();
      }));

    return templateRequest.pipe(
      map((response: any): SafeHtml | null => {
        if (response && response.success === 'true' && response.data) {
          // Handle nested array structure: data can be [[{...}]] or [{...}]
          let messagesArray: any[] = [];

          if (Array.isArray(response.data)) {
            if (response.data.length > 0) {
              // Check if data[0] is an array (nested structure like [[{...}]])
              if (Array.isArray(response.data[0])) {
                messagesArray = response.data[0];
              } else {
                // Direct array structure like [{...}]
                messagesArray = response.data;
              }

              // Find the message with matching nPortalMessageId
              if (Array.isArray(messagesArray) && messagesArray.length > 0) {
                const message = messagesArray.find((msg: any) => msg && msg.nPortalMessageId === messageId);
                if (message && message.sMessageBody) {
                  const safeHtml = this.sanitizer.bypassSecurityTrustHtml(message.sMessageBody);
                  // Cache the result if we have a cache key
                  if (cacheKey) {
                    this.templateMessageCache.set(cacheKey, safeHtml);
                  }
                  return safeHtml;
                }
              }
            }
          }
        }
        // Cache null result as well to avoid repeated API calls
        if (cacheKey) {
          this.templateMessageCache.set(cacheKey, null);
        }
        return null;
      }),
      catchError((error: any): Observable<SafeHtml | null> => {
        console.error('Error fetching template messages:', error);
        // Cache null on error to avoid repeated failed calls
        if (cacheKey) {
          this.templateMessageCache.set(cacheKey, null);
        }
        return new Observable<SafeHtml | null>(observer => {
          observer.next(null);
          observer.complete();
        });
      })
    );
  }

  /**
   * Clear template message cache (useful when tenant changes or on manual refresh)
   */
  clearTemplateMessageCache(tenantCode?: string): void {
    if (tenantCode) {
      // PE-6337 — cache keys use plain tenantCode
      const keysToDelete: string[] = [];
      this.templateMessageCache.forEach((value, key) => {
        if (key.includes(tenantCode)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.templateMessageCache.delete(key));
    } else {
      this.templateMessageCache.clear();
    }
  }

  /**
   * Patient MFA — uses login SessionId in sessionid header (see session.interceptor + pending_mfa_login).
   */
  getPatientMfaContacts(npatientid: string, tenantcode: string, patientRepresentative = false): Observable<PatientMfaOtpResponse> {
    const q = [
      `npatientid=${encodeURIComponent(npatientid)}`,
      `tenantcode=${encodeURIComponent(tenantcode)}`,
      `patientRepresentative=${patientRepresentative}`
    ].join('&');
    return this.http.get<PatientMfaOtpResponse>(`${this.apiUrl}/auth/patient-mfa/contacts?${q}`);
  }

  sendPatientMfaOtp(request: PatientMfaSendOtpRequest): Observable<PatientMfaOtpResponse> {
    return this.http.post<PatientMfaOtpResponse>(`${this.apiUrl}/auth/patient-mfa/send-otp`, request);
  }

  validatePatientMfaOtp(request: PatientMfaValidateOtpRequest): Observable<PatientMfaOtpResponse> {
    return this.http.post<PatientMfaOtpResponse>(`${this.apiUrl}/auth/patient-mfa/validate-otp`, request);
  }

  getPatientMfaPreference(npatientid: string, tenantcode: string): Observable<PatientMfaPreferenceResponse> {
    const q = `npatientid=${encodeURIComponent(npatientid)}&tenantcode=${encodeURIComponent(tenantcode)}`;
    return this.http.get<PatientMfaPreferenceResponse>(`${this.apiUrl}/auth/patient-mfa/get-preference?${q}`);
  }

  savePatientMfaPreference(request: PatientMfaPreferenceRequest): Observable<PatientMfaPreferenceResponse> {
    return this.http.put<PatientMfaPreferenceResponse>(
      `${this.apiUrl}/auth/patient-mfa/save-preference`,
      request
    );
  }

  validatePatientMfaChannel(request: PatientMfaChannelRequest): Observable<PatientMfaChannelValidationResponse> {
    const q = new URLSearchParams({
      npatientid: request.npatientid,
      tenantcode: request.tenantcode,
      channelType: request.channelType
    });
    if (request.smsCountryCode) {
      q.append('smsCountryCode', request.smsCountryCode);
    }
    q.append('patientRepresentative', String(!!request.patientRepresentative));
    return this.http.get<PatientMfaChannelValidationResponse>(`${this.apiUrl}/auth/patient-mfa/validate-channel?${q.toString()}`);
  }

  // PE-6154 — Forgot Username Step 1: verify identity (email XOR security path)
  verifyForgotUsername(
    firstName: string,
    lastName: string,
    dobIso: string,
    tenantCode: string,
    options?: {
      email?: string;
      securityQuestion?: string;
      securityAnswer?: string;
    }
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    const payload: Record<string, string> = {
      firstname: await this.encryptionService.encryptAes256Gcm(firstName),
      lastname: await this.encryptionService.encryptAes256Gcm(lastName),
      dob: await this.encryptionService.encryptAes256Gcm(dobIso),
      tenantcode: await this.encryptionService.encryptAes256Gcm(tenantCode)
    };
    const email = options?.email?.trim();
    if (email) {
      payload['email'] = await this.encryptionService.encryptAes256Gcm(email);
    }
    const securityQuestion = options?.securityQuestion?.trim();
    const securityAnswer = options?.securityAnswer?.trim();
    if (securityQuestion && securityAnswer) {
      payload['securityquestion'] = await this.encryptionService.encryptAes256Gcm(securityQuestion);
      payload['securityanswer'] = await this.encryptionService.encryptAes256Gcm(securityAnswer);
    }
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/auth/forgot-username/verify`, payload));
  
    })());
  }

  // PE-6154 — Forgot Username Step 2: send OTP (also resend)
  sendForgotUsernameOtp(
    loginNameEnc: string,
    patientPortalAccessIdEnc: string,
    registeredEmailEnc: string,
    tenantCode: string,
    otpSessionIdEnc?: string
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    const payload: Record<string, string> = {
      loginname: loginNameEnc,
      patientportalaccessid: patientPortalAccessIdEnc,
      registeredemail: registeredEmailEnc,
      tenantcode: await this.encryptionService.encryptAes256Gcm(tenantCode)
    };
    if (otpSessionIdEnc) {
      payload['otpsessionid'] = otpSessionIdEnc;
    }
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/auth/forgot-username/send-otp`, payload));
  
    })());
  }

  // PE-6154 — Forgot Username Step 3: validate OTP and email username
  validateForgotUsernameOtp(
    loginNameEnc: string,
    patientPortalAccessIdEnc: string,
    registeredEmailEnc: string,
    otpSessionIdEnc: string,
    otp: string,
    tenantCode: string
  ): Observable<any> {
    // PE-6337 — AES-GCM async
    return from((async () => {
    const payload = {
      loginname: loginNameEnc,
      patientportalaccessid: patientPortalAccessIdEnc,
      registeredemail: registeredEmailEnc,
      otpsessionid: otpSessionIdEnc,
      otp: otp,
      tenantcode: await this.encryptionService.encryptAes256Gcm(tenantCode)
    };
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/auth/forgot-username/validate-otp`, payload));
  
    })());
  }

}
